import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTranslationFromTranslatableEntity } from '../constants/matter';
import { InvoiceableType } from '../constants/transaction';
import { useLanguage } from '../context/LanguageContext';
import { EntityStatus } from '../interface/common';
import { IFilterOption } from '../interface/transaction';
import { useFetchMatterRequestTypes } from './useFetchMatterRequestTypes';

const NO_MATTER_TYPE_IDS: string[] = [];

interface IUseMatterFilters {
  initialInvoiceableType?: string;
  initialMatterTypeIds?: string[];
  matterTypeSearch?: string;
}

export const useMatterFilters = ({
  initialInvoiceableType = '',
  initialMatterTypeIds = NO_MATTER_TYPE_IDS,
  matterTypeSearch,
}: IUseMatterFilters = {}) => {
  const { t } = useTranslation();
  const { language: appLanguage } = useLanguage();

  const invoiceableTypeFilters: IFilterOption[] = useMemo(
    () => [
      { label: t('matter:invoiceableType.billable'), value: InvoiceableType.BILLABLE },
      { label: t('matter:invoiceableType.internal'), value: InvoiceableType.INTERNAL },
      { label: t('matter:invoiceableType.nonBillable'), value: InvoiceableType.NON_BILLABLE },
    ],
    [t],
  );
  const [selectedInvoiceableType, setSelectedInvoiceableType] = useState<IFilterOption | undefined>(
    () => invoiceableTypeFilters.find((option) => option.value === initialInvoiceableType),
  );
  const [selectedMatterTypes, setSelectedMatterTypes] = useState<IFilterOption[]>([]);
  const hasRestoredMatterTypeFilter = useRef(false);

  const {
    data: paginatedMatterTypesData,
    fetchNextPage: loadMoreMatterTypes,
    hasNextPage: hasMoreMatterTypes,
    isFetchingNextPage: isFetchingMoreMatterTypes,
  } = useFetchMatterRequestTypes(
    {
      pageSize: 100,
      search: matterTypeSearch,
      extraFilters: {
        status: EntityStatus.ACTIVE,
      },
    },
    true,
  );
  const matterTypeFilters: IFilterOption[] = useMemo(
    () =>
      paginatedMatterTypesData.map((matterType) => ({
        label: getTranslationFromTranslatableEntity(matterType.translations, appLanguage),
        value: matterType.id,
      })),
    [paginatedMatterTypesData, appLanguage],
  );

  useEffect(() => {
    if (hasRestoredMatterTypeFilter.current || initialMatterTypeIds.length === 0) return;
    if (matterTypeFilters.length === 0) return;
    const restoredMatterTypes = matterTypeFilters.filter((option) =>
      initialMatterTypeIds.includes(option.value),
    );
    if (restoredMatterTypes.length > 0) {
      setSelectedMatterTypes(restoredMatterTypes);
    }
    hasRestoredMatterTypeFilter.current = true;
  }, [matterTypeFilters, initialMatterTypeIds]);

  const matterFilterParams: Record<string, string> = {
    ...(selectedInvoiceableType ? { invoiceableType: selectedInvoiceableType.value } : {}),
    ...(selectedMatterTypes.length
      ? { matterTypeId: `$in:${selectedMatterTypes.map((x) => x.value).join(',')}` }
      : {}),
  };

  const selectedMatterFilters: IFilterOption[] = Array.from(
    new Map(
      [...(selectedInvoiceableType ? [selectedInvoiceableType] : []), ...selectedMatterTypes].map(
        (item) => [item.value, item],
      ),
    ).values(),
  );

  const removeMatterFilter = (value: string) => {
    setSelectedMatterTypes((prev) => prev.filter((item) => item.value !== value));
    if (selectedInvoiceableType?.value === value) {
      setSelectedInvoiceableType(undefined);
    }
  };

  const clearMatterFilters = () => {
    setSelectedInvoiceableType(undefined);
    setSelectedMatterTypes([]);
  };

  return {
    invoiceableTypeFilters,
    matterTypeFilters,
    selectedInvoiceableType,
    setSelectedInvoiceableType,
    selectedMatterTypes,
    setSelectedMatterTypes,
    selectedMatterFilters,
    matterFilterParams,
    removeMatterFilter,
    clearMatterFilters,
    hasMoreMatterTypes,
    isFetchingMoreMatterTypes,
    loadMoreMatterTypes,
  };
};
